import { useMemo } from "react";
import { useQuery, useQueries, useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { apiService } from "../services/api";
import type { App, Template, LandingTemplate, StaticSite, CreateStaticSitePayload, AppConfigTemplate } from "../types/api";
import type { TemplateDynamicFields } from "../types";

const STALE_TIME_MS = 2 * 60 * 1000; // 2 хвилини

/**
 * Мапа id → ім'я worker-шаблону.
 * Спочатку бере зі списку all-versions; для шаблонів, яких там немає
 * (список відфільтрований по департаменту CRM), довантажує по одному
 * через GET /templates/:id. Якщо шаблон не знайдено — лишається ID.
 */
export function useWorkerTemplateNameMap(
  templateIds: Array<string | undefined | null>,
): Map<string, string> {
  const templatesQuery = useTemplatesAllVersions();

  const idsKey = templateIds.filter(Boolean).join("|");
  const uniqueIds = useMemo(
    () =>
      Array.from(
        new Set(idsKey.split("|").filter((x): x is string => Boolean(x))),
      ),
    [idsKey],
  );

  const knownIds = useMemo(() => {
    const set = new Set<string>();
    (templatesQuery.data ?? []).forEach((t: any) => {
      if (t?.id) set.add(t.id);
    });
    return set;
  }, [templatesQuery.data]);

  const missingIds = uniqueIds.filter((id) => !knownIds.has(id));

  const extraQueries = useQueries({
    queries: missingIds.map((id) => ({
      queryKey: ["template", id],
      queryFn: () => apiService.getTemplate(id),
      staleTime: 5 * 60 * 1000,
      retry: false,
    })),
  });

  return useMemo(() => {
    const map = new Map<string, string>();
    (templatesQuery.data ?? []).forEach((t: any) => {
      if (t?.id) map.set(t.id, t.name || t.id);
    });
    extraQueries.forEach((q) => {
      const data: any = q.data;
      if (data?.id) map.set(data.id, data.name || data.id);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templatesQuery.data, ...extraQueries.map((q) => q.data)]);
}

/** Query key для template dynamic fields (один вигляд ключа для getQueryData / invalidateQueries / fetchQuery). */
export function getTemplateDynamicFieldsQueryKey(
  templateId: string | null | undefined,
) {
  return ["templateDynamicFields", templateId] as const;
}

/** Спільні опції запиту template dynamic fields (useQuery + імперативний fetch). */
export function templateDynamicFieldsQueryOptions(
  templateId: string | null | undefined,
) {
  return {
    queryKey: getTemplateDynamicFieldsQueryKey(templateId),
    queryFn: () => apiService.getTemplateDynamicFields(templateId!),
    staleTime: STALE_TIME_MS,
  };
}

/** Імперативне завантаження структури шаблону (endpoints + dynamicFields). Використовує той самий кеш, що й useTemplateDynamicFields. */
export function fetchTemplateDynamicFields(
  queryClient: QueryClient,
  templateId: string,
): Promise<TemplateDynamicFields> {
  return queryClient.fetchQuery({
    ...templateDynamicFieldsQueryOptions(templateId),
  });
}

export function useAppsList() {
  return useQuery({
    queryKey: ["apps"],
    queryFn: () => apiService.getApps(),
    staleTime: STALE_TIME_MS,
  });
}

export function useRefreshAppsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiService.getApps({ refresh: true }),
    onSuccess: (data) => {
      queryClient.setQueryData(["apps"], data);
    },
  });
}

export function useTemplatesAllVersions() {
  return useQuery({
    queryKey: ["templates", "all-versions"],
    queryFn: () => apiService.getTemplatesAllVersions(),
    staleTime: STALE_TIME_MS,
  });
}

export function useTemplatesList() {
  return useQuery<Template[]>({
    queryKey: ["templates"],
    queryFn: () => apiService.getTemplates(),
    staleTime: STALE_TIME_MS,
  });
}

export function useLandingTemplates() {
  return useQuery({
    queryKey: ["landingTemplates"],
    queryFn: () => apiService.getLandingTemplates(),
    staleTime: STALE_TIME_MS,
  });
}

/** Одна апка за id. Кеш увімкнено (staleTime 2 хв, refetchOnMount: false) — форма скидається при зміні id, потім заповнюється з кешу або мережі. */
export function useApp(appId: string | null | undefined) {
  return useQuery({
    queryKey: ["app", appId],
    queryFn: () => apiService.getApp(appId!),
    enabled: !!appId,
    staleTime: STALE_TIME_MS,
    refetchOnMount: false,
  });
}

/** Static sites для апки. Кеш увімкнено. */
export function useStaticSites(appId: string | null | undefined) {
  return useQuery({
    queryKey: ["staticSites", appId],
    queryFn: () => apiService.getStaticSites(appId!),
    enabled: !!appId,
    staleTime: STALE_TIME_MS,
    refetchOnMount: false,
  });
}

/** Структура шаблону (endpoints + dynamicFields). Без appId — тільки структура; значення з апки підставляють у формі. */
export function useTemplateDynamicFields(
  templateId: string | null | undefined,
) {
  return useQuery<TemplateDynamicFields>({
    ...templateDynamicFieldsQueryOptions(templateId),
    enabled: !!templateId,
    refetchOnMount: false,
  });
}

/** Мутація видалення апки. Після успіху інвалідує список апок і видаляє кеш app/staticSites для цієї апки. */
export function useDeleteAppMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (appId: string) => apiService.deleteApp(appId),
    onSuccess: (_, appId: string) => {
      queryClient.invalidateQueries({ queryKey: ["apps"] });
      queryClient.removeQueries({ queryKey: ["app", appId] });
      queryClient.removeQueries({ queryKey: ["staticSites", appId] });
    },
  });
}

/** Мутація деплою апки. Після успіху інвалідує список апок і дані цієї апки — React Query сам рефетчить і контролює стейт. */
export function useDeployAppMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (appId: string) => apiService.deployApp(appId, true),
    onSuccess: (_, appId) => {
      queryClient.invalidateQueries({ queryKey: ["apps"] });
      queryClient.invalidateQueries({ queryKey: ["app", appId] });
      queryClient.invalidateQueries({ queryKey: ["staticSites", appId] });
      queryClient.invalidateQueries({ queryKey: ["workerCode", appId] });
    },
  });
}

export function useDeployBulkMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { appIds: string[]; confirmed?: boolean }) =>
      apiService.deployBulk({ ...body, confirmed: body.confirmed ?? true }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["apps"] });
      variables.appIds.forEach((appId) => {
        queryClient.invalidateQueries({ queryKey: ["workerCode", appId] });
      });
    },
  });
}

/** Мутація масового видалення (delete-bulk). Приймає appIds (до 150), відправляє POST /apps/delete-bulk. Після успіху інвалідує список апок. */
export function useDeleteBulkMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { appIds: string[]; confirmed?: boolean }) =>
      apiService.deleteBulk({ ...body, confirmed: body.confirmed ?? true }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["apps"] });
      variables.appIds.forEach((appId) => {
        queryClient.removeQueries({ queryKey: ["app", appId] });
        queryClient.removeQueries({ queryKey: ["staticSites", appId] });
      });
    },
  });
}

/** Мутація створення апки. Після успіху інвалідує список апок. */
export function useCreateAppMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<App>) => apiService.createApp(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["apps"] });
    },
  });
}

/** Мутація оновлення апки. Після успіху інвалідує список апок, app та staticSites для цієї апки. */
export function useUpdateAppMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<App> }) =>
      apiService.updateApp(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["apps"] });
      queryClient.invalidateQueries({ queryKey: ["app", id] });
      queryClient.invalidateQueries({ queryKey: ["staticSites", id] });
    },
  });
}

/** Відповідь syncAppEndpoints. */
export type SyncAppEndpointsResponse = Awaited<
  ReturnType<typeof apiService.syncAppEndpoints>
>;

/** Worker code для апки. enabled: false якщо appId порожній — використовувати з станом модалки (appId при відкритті). */
export function useWorkerCode(appId: string | null | undefined) {
  return useQuery({
    queryKey: ["workerCode", appId],
    queryFn: () => apiService.getWorkerCode(appId!),
    enabled: !!appId,
    staleTime: 30 * 1000,
  });
}

/** Мутація синхронізації ендпоінтів апки з шаблоном. Після успіху інвалідує app та staticSites. */
export function useSyncAppEndpointsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      appId,
      templateId,
    }: {
      appId: string;
      templateId?: string;
    }) => apiService.syncAppEndpoints(appId, templateId),
    onSuccess: (_, { appId }) => {
      queryClient.invalidateQueries({ queryKey: ["app", appId] });
      queryClient.invalidateQueries({ queryKey: ["staticSites", appId] });
    },
  });
}

/** Мутація створення статичного сайту. Після успіху інвалідує staticSites для appId. */
export function useCreateStaticSiteMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateStaticSitePayload) => apiService.createStaticSite(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["staticSites", variables.appId] });
    },
  });
}

/** Мутація оновлення статичного сайту. Після успіху інвалідує staticSites для appId. */
export function useUpdateStaticSiteMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data, appId }: { id: string; data: Parameters<typeof apiService.updateStaticSite>[1]; appId: string }) =>
      apiService.updateStaticSite(id, data),
    onSuccess: (_, { appId }) => {
      queryClient.invalidateQueries({ queryKey: ["staticSites", appId] });
    },
  });
}

/** Мутація видалення статичного сайту. Після успіху інвалідує staticSites для appId. */
export function useDeleteStaticSiteMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, appId }: { id: string; appId: string }) =>
      apiService.deleteStaticSite(id),
    onSuccess: (_, { appId }) => {
      queryClient.invalidateQueries({ queryKey: ["staticSites", appId] });
    },
  });
}

// ===== APP CONFIG TEMPLATES =====

export function useAppConfigTemplates() {
  return useQuery({
    queryKey: ["appConfigTemplates"],
    queryFn: () => apiService.getAppConfigTemplates(),
    staleTime: 2 * 60 * 1000,
  });
}

export function useCreateAppConfigTemplateMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<AppConfigTemplate>) =>
      apiService.createAppConfigTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["appConfigTemplates"] });
    },
  });
}

export function useSaveAppAsTemplateMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ appId, data }: { appId: string; data: { name: string; description?: string; templateId?: string; greyType?: string; endpointMappings?: any[]; landingRoutes?: any[] } }) =>
      apiService.saveAppAsTemplate(appId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["appConfigTemplates"] });
    },
  });
}

export function useDeleteAppConfigTemplateMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiService.deleteAppConfigTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["appConfigTemplates"] });
    },
  });
}
